<script lang="ts">

  import { goto } from "$app/navigation"
  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from "$lib/components/ui/command"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Popover, PopoverButton, PopoverContent } from "$lib/components/ui/popover"
  import { RadioGroup, RadioGroupItem } from "$lib/components/ui/radio-group"
  import { Separator } from "$lib/components/ui/separator"
  import { SeparatorLabel } from "$lib/components/ui/separator-label"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { Relation } from "$lib/data/schema/_relation"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import PlusIcon from "@lucide/svelte/icons/plus"
  import SaveIcon from "@lucide/svelte/icons/save"
  import XIcon from "@lucide/svelte/icons/x"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import DialogFormDocument from "../_components/dialog-form-document.svelte"
  import { type CustomerNoo, type FormData as CustomerNooFormData, type FormDocument, FormDocumentEmpty, FormEmpty, } from "../_types"

  const prefix = $derived(page.params.prefix ?? "0")
  const select = $derived(page.params.id ?? "create")
  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))
  const formWatch = form.useSelector((state) => state.values)

  let documentSelect = $state<FormDocument>(FormDocumentEmpty)
  const documentDialog = useDialog()

  const categories = createQuery(() => ({
    queryKey: ["customer-noo-master-category", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-category`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const bpGroups = createQuery(() => ({
    queryKey: ["customer-noo-master-bp-group", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-bp-group`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const bpClasses = createQuery(() => ({
    queryKey: ["customer-noo-master-bp-class", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-bp-class`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const deliveries = createQuery(() => ({
    queryKey: ["customer-noo-master-delivery", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-delivery`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const paymentTerms = createQuery(() => ({
    queryKey: ["customer-noo-master-payment-term", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-payment-term`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const divisions = createQuery(() => ({
    queryKey: ["customer-noo-master-division", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-division`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const provinces = createQuery(() => ({
    queryKey: ["customer-noo-master-province", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-province`)
      const responseData = response.data.result!
      return responseData
    },
  }))
  const citySearch = createSearch("")
  const cities = createQuery(() => ({
    enabled: formWatch.current.province_id > 0,
    queryKey: ["customer-noo-master-city", {
      prefix,
      province_id: formWatch.current.province_id,
      search: citySearch.debounced
    }],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-city`, {
        params: {
          province_id: formWatch.current.province_id,
          search: citySearch.debounced,
        },
      })
      const responseData = response.data.result!
      return responseData
    },
  }))

  const query = createQuery(() => ({
    queryKey: ["customer-noo", prefix, select],
    queryFn: async () => {

      const response = await httpClient.get<_response<CustomerNoo>>(`/api/${prefix}/customer-noos/${select}`)
      const responseData = response.data.result!

      form.reset(FormEmpty)
      form.setFieldValue("id", responseData.id)
      form.setFieldValue("number", responseData.number)
      form.setFieldValue("datetime", responseData.datetime)
      form.setFieldValue("name", responseData.name)
      form.setFieldValue("category_id", responseData.category.id)
      form.setFieldValue("category_name", responseData.category.name)
      form.setFieldValue("division_id", responseData.division.id)
      form.setFieldValue("division_name", responseData.division.name)
      form.setFieldValue("bp_group_id", responseData.bp_group.id)
      form.setFieldValue("bp_group_name", responseData.bp_group.name)
      form.setFieldValue("bp_class_id", responseData.bp_class.id)
      form.setFieldValue("bp_class_name", responseData.bp_class.name)
      form.setFieldValue("delivery_id", responseData.delivery.id)
      form.setFieldValue("delivery_name", responseData.delivery.name)
      form.setFieldValue("payment_limit", responseData.payment_limit)
      form.setFieldValue("payment_term_id", responseData.payment_term.id)
      form.setFieldValue("payment_term_name", responseData.payment_term.name)
      form.setFieldValue("tax_name", responseData.tax_name)
      form.setFieldValue("tax_number", responseData.tax_number)
      form.setFieldValue("contact_name", responseData.contact_name)
      form.setFieldValue("contact_phone", responseData.contact_phone)
      form.setFieldValue("province_id", responseData.province.id)
      form.setFieldValue("province_name", responseData.province.name)
      form.setFieldValue("city_id", responseData.city.id)
      form.setFieldValue("city_name", responseData.city.name)
      form.setFieldValue("address", responseData.address)
      form.setFieldValue("coordinate", responseData.coordinate)
      form.setFieldValue("radius", responseData.radius)
      form.setFieldValue("documents", responseData.documents)

      return ""
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: CustomerNooFormData) => {

      const id = select === "create" ? "" : select
      const response = await httpClient.postForm<_response<null>>(`/api/${prefix}/customer-noos/${id}`, {
        _method: id === "" ? "POST" : "PATCH",
        ...data,
      })
      const responseData = response.data

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) return

      toast.success(responseData.message)
      await goto(`/${prefix}/customer-noo`, { replaceState: true })

      return
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message,
      })
    },
  }))

  function handleDocument(document: FormDocument = FormDocumentEmpty) {
    documentSelect = document
    documentDialog.setOpen(true)
  }

  function handleDocumentSave(document: FormDocument) {
    const documents = [...form.state.values.documents]
    const index = documents.findIndex((item) => item.ref_id === document.ref_id)

    if (index === -1) {
      documents.push(document)
    } else {
      documents[index] = document
    }

    form.setFieldValue("documents", documents)
  }

  function handleDocumentRemove(document: FormDocument) {
    const documents = form.state.values.documents.filter((item) => item.ref_id !== document.ref_id)

    form.setFieldValue("documents", documents)
  }

</script>

<Loader
  show={
    query.isFetching ||
    categories.isFetching ||
    bpGroups.isFetching ||
    bpClasses.isFetching ||
    deliveries.isFetching ||
    paymentTerms.isFetching ||
    divisions.isFetching ||
    provinces.isFetching ||
    mutation.isPending
  }
  className="z-20"/>
<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/customer-noo`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Tutup">
          <XIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Noo Form</h1>
      </div>
      <div>
        <Button
          variant="ghost"
          size="icon"
          class="rounded-full"
          aria-label="Simpan"
          disabled={mutation.isPending}
          onclick={() => mutation.mutate(formWatch.current)}>
          <SaveIcon aria-hidden="true"/>
        </Button>
      </div>
    </div>
  </nav>
  <div class="h-4"></div>
  <form
    action="#"
    autocomplete="off"
    class="px-3 space-y-3"
    onsubmit={(event) => {
      event.preventDefault()
      mutation.mutate(formWatch.current)
    }}>
    <div class="grid grid-cols-2 gap-3">
      <form.Field name="number">
        {#snippet children(field)}
          <div>
            <FieldLabel>Nomor</FieldLabel>
            <Input
              name={field.name}
              value={field.state.value}
              readonly
              class="uppercase tabular-nums tracking-tight"/>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
      <form.Field name="datetime">
        {#snippet children(field)}
          <div>
            <FieldLabel>Tanggal</FieldLabel>
            <Input
              name={field.name}
              value={field.state.value.toDateFormat("dd/MM/yyyy")}
              readonly
              class="tabular-nums tracking-tight"/>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
    </div>
    <form.Field name="name">
      {#snippet children(field)}
        <div>
          <FieldLabel>Nama</FieldLabel>
          <Input
            name={field.name}
            value={field.state.value}
            oninput={(event) => field.handleChange(event.currentTarget.value)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <div>
      <FieldLabel>Kategori</FieldLabel>
      <Popover>
        <PopoverButton
          value={formWatch.current.category_name}
          placeholder="Pilih Kategori"/>
        <PopoverContent>
          <Command>
            <CommandInput placeholder="Cari..."/>
            <CommandList class="min-h-20">
              <CommandEmpty>Tidak ada data</CommandEmpty>
              <CommandGroup>
                {#each categories.data ?? [] as category (category.id)}
                  <CommandItem
                    value={category.name}
                    onclick={() => {
                      form.setFieldValue("category_id", category.id)
                      form.setFieldValue("category_name", category.name)
                      document.keyEsc()
                    }}>
                    {category.name}
                  </CommandItem>
                {/each}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldMessage
        name="category_id"
        form={form}/>
    </div>
    <div class="grid grid-cols-2 gap-3">
      <div>
        <FieldLabel>BP Group</FieldLabel>
        <Popover>
          <PopoverButton
            value={formWatch.current.bp_group_name}
            placeholder="Pilih Group"/>
          <PopoverContent>
            <Command>
              <CommandInput placeholder="Cari..."/>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each bpGroups.data ?? [] as group (group.id)}
                    <CommandItem
                      value={group.name}
                      onclick={() => {
                        form.setFieldValue("bp_group_id", group.id)
                        form.setFieldValue("bp_group_name", group.name)
                        document.keyEsc()
                      }}>
                      {group.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <FieldMessage
          name="bp_group_id"
          form={form}/>
      </div>
      <div>
        <FieldLabel>BP Class</FieldLabel>
        <Popover>
          <PopoverButton
            value={formWatch.current.bp_class_name}
            placeholder="Pilih Class"/>
          <PopoverContent>
            <Command>
              <CommandInput placeholder="Cari..."/>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each bpClasses.data ?? [] as bpClass (bpClass.id)}
                    <CommandItem
                      value={bpClass.name}
                      onclick={() => {
                        form.setFieldValue("bp_class_id", bpClass.id)
                        form.setFieldValue("bp_class_name", bpClass.name)
                        document.keyEsc()
                      }}>
                      {bpClass.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <FieldMessage
          name="bp_class_id"
          form={form}/>
      </div>
    </div>
    <div>
      <FieldLabel>Pengiriman</FieldLabel>
      <RadioGroup
        name="delivery_id"
        value={String(formWatch.current.delivery_id)}
        class="flex flex-col">
        {#each deliveries.data ?? [] as delivery (delivery.id)}
          <label class="flex items-start gap-2 text-sm">
            <RadioGroupItem
              value={String(delivery.id)}
              class="mt-0.5"
              onclick={() => {
                form.setFieldValue("delivery_id", delivery.id)
                form.setFieldValue("delivery_name", delivery.name)
              }}
            />
            <span>{delivery.name}</span>
          </label>
        {/each}
      </RadioGroup>
      <FieldMessage
        name="delivery_id"
        form={form}/>
    </div>
    <div class="grid grid-cols-2 gap-3">
      <form.Field name="payment_limit">
        {#snippet children(field)}
          <div>
            <FieldLabel>Credit Limit</FieldLabel>
            <Input
              name={field.name}
              inputmode="numeric"
              class="tabular-nums tracking-tight"
              value={field.state.value.toLocaleString("en-US")}
              oninput={(event) => field.handleChange(Number(event.currentTarget.value.replaceAll(",", "")) || 0)}/>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
      <div>
        <FieldLabel>Payment Terms</FieldLabel>
        <Popover>
          <PopoverButton
            value={formWatch.current.payment_term_name}
            placeholder="Pilih Term"/>
          <PopoverContent>
            <Command>
              <CommandInput placeholder="Cari..."/>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each paymentTerms.data ?? [] as paymentTerm (paymentTerm.id)}
                    <CommandItem
                      value={paymentTerm.name}
                      onclick={() => {
                        form.setFieldValue("payment_term_id", paymentTerm.id)
                        form.setFieldValue("payment_term_name", paymentTerm.name)
                        document.keyEsc()
                      }}>
                      {paymentTerm.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <FieldMessage
          name="payment_term_id"
          form={form}/>
      </div>
    </div>
    <SeparatorLabel label="Pajak, kontak dan alamat"/>
    <div class="grid grid-cols-2 gap-3">
      <form.Field name="tax_name">
        {#snippet children(field)}
          <div>
            <FieldLabel>Nama Pajak</FieldLabel>
            <Input
              name={field.name}
              value={field.state.value}
              oninput={(event) => field.handleChange(event.currentTarget.value)}/>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
      <form.Field name="tax_number">
        {#snippet children(field)}
          <div>
            <FieldLabel>NPWP</FieldLabel>
            <Input
              name={field.name}
              class="tabular-nums tracking-tight"
              value={field.state.value}
              oninput={(event) => field.handleChange(event.currentTarget.value)}/>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
    </div>
    <div>
      <FieldLabel>Divisi</FieldLabel>
      <Popover>
        <PopoverButton
          value={formWatch.current.division_name}
          placeholder="Pilih Divisi"/>
        <PopoverContent>
          <Command>
            <CommandInput placeholder="Cari..."/>
            <CommandList class="min-h-20">
              <CommandEmpty>Tidak ada data</CommandEmpty>
              <CommandGroup>
                {#each divisions.data ?? [] as division (division.id)}
                  <CommandItem
                    value={division.name}
                    onclick={() => {
                      form.setFieldValue("division_id", division.id)
                      form.setFieldValue("division_name", division.name)
                      document.keyEsc()
                    }}>
                    {division.name}
                  </CommandItem>
                {/each}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldMessage
        name="division_id"
        form={form}/>
    </div>
    <form.Field name="contact_name">
      {#snippet children(field)}
        <div>
          <FieldLabel>Nama</FieldLabel>
          <Input
            name={field.name}
            value={field.state.value}
            oninput={(event) => field.handleChange(event.currentTarget.value)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <form.Field name="contact_phone">
      {#snippet children(field)}
        <div>
          <FieldLabel>Telepon</FieldLabel>
          <Input
            name={field.name}
            value={field.state.value}
            oninput={(event) => field.handleChange(event.currentTarget.value)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <div class="grid grid-cols-2 gap-3">
      <div>
        <FieldLabel>Provinsi</FieldLabel>
        <Popover>
          <PopoverButton
            value={formWatch.current.province_name}
            placeholder="Pilih Provinsi"/>
          <PopoverContent>
            <Command>
              <CommandInput placeholder="Cari..."/>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each provinces.data ?? [] as province (province.id)}
                    <CommandItem
                      value={province.name}
                      onclick={() => {
                        form.setFieldValue("province_id", province.id)
                        form.setFieldValue("province_name", province.name)
                        form.setFieldValue("city_id", 0)
                        form.setFieldValue("city_name", "")
                        citySearch.set("")
                        document.keyEsc()
                      }}>
                      {province.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <FieldMessage
          name="province_id"
          form={form}/>
      </div>
      <div>
        <FieldLabel>Kota</FieldLabel>
        <Popover>
          <PopoverButton
            value={formWatch.current.city_name}
            placeholder="Pilih Kota"
            disabled={!formWatch.current.province_id}/>
          <PopoverContent>
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Cari..."
                value={citySearch.state}
                oninput={(event) => citySearch.set(event.currentTarget.value)}/>
              <CommandList class="min-h-20">
                <CommandEmpty>Tidak ada data</CommandEmpty>
                <CommandGroup>
                  {#each cities.data ?? [] as city (city.id)}
                    <CommandItem
                      value={city.name}
                      onclick={() => {
                        form.setFieldValue("city_id", city.id)
                        form.setFieldValue("city_name", city.name)
                        document.keyEsc()
                      }}>
                      {city.name}
                    </CommandItem>
                  {/each}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <FieldMessage
          name="city_id"
          form={form}/>
      </div>
    </div>
    <form.Field name="address">
      {#snippet children(field)}
        <div>
          <FieldLabel>Alamat</FieldLabel>
          <Textarea
            name={field.name}
            value={field.state.value}
            oninput={(event) => field.handleChange(event.currentTarget.value)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <SeparatorLabel label="Koordinat dan dokumentasi"/>
    <div class="grid grid-cols-2 gap-3">
      <form.Field name="coordinate">
        {#snippet children(field)}
          <div>
            <FieldLabel>Koordinat Latitude</FieldLabel>
            <Input
              type="number"
              step="any"
              value={field.state.value.latitude}
              oninput={(event) =>
                form.setFieldValue("coordinate", {
                  ...form.getFieldValue("coordinate"),
                  latitude: Number(event.currentTarget.value),
                })}/>
            <FieldMessage
              name={field.name}
              form={form}/>
          </div>
        {/snippet}
      </form.Field>
      <div>
        <FieldLabel>Koordinat Longitude</FieldLabel>
        <Input
          type="number"
          step="any"
          value={formWatch.current.coordinate.longitude}
          oninput={(event) =>
            form.setFieldValue("coordinate", {
              ...form.getFieldValue("coordinate"),
              longitude: Number(event.currentTarget.value),
            })}/>
      </div>
    </div>
    <form.Field name="radius">
      {#snippet children(field)}
        <div>
          <FieldLabel>Radius (m)</FieldLabel>
          <Input
            type="number"
            min="0"
            value={field.state.value}
            oninput={(event) => field.handleChange(Number(event.currentTarget.value) || 0)}/>
          <FieldMessage
            name={field.name}
            form={form}/>
        </div>
      {/snippet}
    </form.Field>
    <Separator/>
    <div class="flex items-center justify-between border-b pb-1">
      <h3>Dokumentasi</h3>
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        class="shadow-none"
        aria-label="Tambah dokumen"
        onclick={() => handleDocument()}>
        <PlusIcon aria-hidden="true"/>
      </Button>
    </div>
    <div class="space-y-2">
      {#if formWatch.current.documents.isEmpty()}
        <div class="block rounded-md border px-3 py-2 text-center text-sm">Tidak ada data</div>
      {:else}
        {#each formWatch.current.documents as document (document.ref_id)}
          <Button
            type="button"
            variant="outline"
            class="h-auto w-full justify-between px-3 py-2 text-start"
            onclick={() => handleDocument(document)}>
            <span>
              <span class="block">{document.name}</span>
              <span class="block text-muted-foreground">{document.description || "-"}</span>
            </span>
          </Button>
        {/each}
      {/if}
    </div>
    <Separator/>
    <div class="flex justify-between gap-2">
      <div class="flex-1"></div>
      <Button
        type="submit"
        class="group"
        data-loading={mutation.isPending}
        disabled={mutation.isPending}>
        <LoaderCircleIcon
          class="invisible absolute animate-spin group-data-[loading=true]:visible"
          aria-hidden="true"/>
        <span class="group-data-[loading=true]:invisible">Simpan</span>
      </Button>
    </div>
    <div class="h-20"></div>
  </form>
</main>
<DialogFormDocument
  open={documentDialog.state}
  setOpen={documentDialog.setOpen}
  select={documentSelect}
  onsave={handleDocumentSave}
  onremove={handleDocumentRemove}/>
